import { memo, type ChangeEvent, useCallback, useMemo } from 'react';
import { Switch } from '@mui/material';
import type {
  Flow,
  LoadHandlingPoiSpec as LoadHandlingPoiSpecType,
  SingularStationArea,
} from '@thive/fddm-types';
import { useTranslation } from 'react-i18next';

import { GapHorizontalIcon } from '@/assets/icons';
import {
  QUARTER_ROTATION_RADIANS,
  THREE_QUARTER_ROTATION_RADIANS,
} from '@/helpers/angleConstants';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { NumberInput } from '@/modules/common/components/inputs/components/NumberInput';
import { NumUtils } from '@/modules/common/utils';
import {
  getStationCarrierSide,
  getStationFootprintFromLoadType,
  getStationWidthForCarrierSide,
} from '@/modules/floorplanDesign/helpers/stationCapacity';
import { rotateConstellation } from '@/modules/floorplanDesign/helpers/rotateConstellation';
import { getStoredPoiAngle } from '@/modules/floorplanDesign/helpers/poiAngleHelpers';
import { useReadSelectedAreas } from '@/modules/floorplanDesign/hooks';
import { areasToFlatCollection, isSingularStationArea } from '@/modules/floorplanDesign/helpers';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { useAppStore } from '@/modules/app/store/useAppStore';
import { SnackbarServiceFactory } from '@/modules/snackbar/service/SnackbarServiceFactory';

interface LoadHandlingPoiSpecProps {
  selectedArea?: SingularStationArea;
}

function LoadHandlingPoiSpecComponent({ selectedArea }: LoadHandlingPoiSpecProps) {
  const mode = useAppStore((state) => state.mode);
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);
  const selectedAreas = useReadSelectedAreas();
  const snackbarSvc = useMemo(
    () => SnackbarServiceFactory.getInstance().createSnackbarService(),
    [],
  );
  const { getRelatedFlows } = useFloorplanDesignStore((state) => state);
  const { t } = useTranslation('interface');

  const isDisabled = useMemo(() => mode !== 'EDITABLE', [mode]);
  const showParkingSetting = useMemo(() => {
    if (!selectedArea) return false;
    return selectedArea.loadHandlingType === 'INTAKE';
  }, [selectedArea]);

  const loadHandlingPoiSpec = selectedArea?.loadHandlingPoiSpecs[0];

  const onDeltaXChange = useCallback(
    (newValueMm: number) => {
      if (!selectedArea || !loadHandlingPoiSpec || Number.isNaN(newValueMm)) return;

      const newValue = NumUtils.mmToMeters(newValueMm);

      fddmSvc.setPreference('singularStationArea.loadHandlingPoiSpecs.deltaX', newValue);

      fddmSvc.updateArea({
        ...selectedArea,
        loadHandlingPoiSpecs: [
          {
            ...loadHandlingPoiSpec,
            deltaX: newValue,
          },
        ],
      });
    },
    [fddmSvc, loadHandlingPoiSpec, selectedArea],
  );

  const onDeltaYChange = useCallback(
    (newValueMm: number) => {
      if (!selectedArea || !loadHandlingPoiSpec || Number.isNaN(newValueMm)) return;

      const newValue = NumUtils.mmToMeters(newValueMm);

      fddmSvc.setPreference('singularStationArea.loadHandlingPoiSpecs.deltaY', newValue);

      fddmSvc.updateArea({
        ...selectedArea,
        loadHandlingPoiSpecs: [
          {
            ...loadHandlingPoiSpec,
            deltaY: newValue,
          },
        ],
      });
    },
    [fddmSvc, loadHandlingPoiSpec, selectedArea],
  );

  const onFitChange = useCallback(
    (
      _event: ChangeEvent<HTMLInputElement>,
      newValue: LoadHandlingPoiSpecType['alwaysFitStation'],
    ) => {
      if (!selectedArea || !loadHandlingPoiSpec) return;
      if (loadHandlingPoiSpec.alwaysFitStation === newValue) return;

      const loadTypeId = selectedArea.stationSpec.loadTypeIds[0];
      const loadType = fddmSvc.getLoadType(loadTypeId);

      let { stationSpec } = selectedArea;
      let nextPoiAngle = loadHandlingPoiSpec.angle;
      let nextConstellationAngle = selectedArea.constellationSpec.angle;

      if (loadType) {
        const carrierSide = getStationCarrierSide(selectedArea, loadType);

        fddmSvc.setPreference(
          'singularStationArea.loadHandlingPoiSpecs.angle',
          getStoredPoiAngle(carrierSide),
        );

        if (newValue) {
          stationSpec = {
            ...selectedArea.stationSpec,
            width: getStationWidthForCarrierSide(loadType, carrierSide),
          };

          if (carrierSide === 'long') {
            nextPoiAngle = 0;
            nextConstellationAngle = rotateConstellation(
              selectedArea.constellationSpec.angle,
              -QUARTER_ROTATION_RADIANS,
            );
          }
        } else {
          stationSpec = {
            ...selectedArea.stationSpec,
            ...getStationFootprintFromLoadType(loadType),
          };

          if (carrierSide === 'long') {
            nextPoiAngle = THREE_QUARTER_ROTATION_RADIANS;
            nextConstellationAngle = rotateConstellation(
              selectedArea.constellationSpec.angle,
              QUARTER_ROTATION_RADIANS,
            );
          }
        }
      }

      fddmSvc.setPreference('singularStationArea.loadHandlingPoiSpecs.alwaysFitStation', newValue);

      fddmSvc.updateArea({
        ...selectedArea,
        stationSpec,
        loadHandlingPoiSpecs: [
          {
            ...loadHandlingPoiSpec,
            angle: nextPoiAngle,
            alwaysFitStation: newValue,
          },
        ],
        constellationSpec: {
          ...selectedArea.constellationSpec,
          angle: nextConstellationAngle,
        },
      });
    },
    [fddmSvc, loadHandlingPoiSpec, selectedArea],
  );

  const onParkingChange = useCallback(
    (_, newValue: LoadHandlingPoiSpecType['canBeUsedForParking']) => {
      const areas = areasToFlatCollection(selectedAreas);
      if (areas.length !== 1) return;
      const area = areas[0];
      if (!isSingularStationArea(area)) return;

      fddmSvc.setPreference(
        'singularStationArea.loadHandlingPoiSpecs.canBeUsedForParking',
        newValue,
      );

      const parkingExtension = newValue
        ? {
            priority: fddmSvc.getPreference(
              'singularStationArea.loadHandlingPoiSpecs.parkingExtension.priority',
            ),
          }
        : undefined;

      fddmSvc.updateArea({
        ...area,
        loadHandlingPoiSpecs: [
          {
            ...area.loadHandlingPoiSpecs[0],
            canBeUsedForParking: newValue,
            parkingExtension,
          },
        ],
      });

      if (newValue) return;

      // reset preAnnounceDuration on related flows
      const updatedFlows: Flow[] = [];
      getRelatedFlows([area.id]).forEach((flow) => {
        if (flow.type === 'TRANSPORT') {
          if (flow.transportExtension && flow.transportExtension.preAnnounceDuration > 0) {
            updatedFlows.push({
              ...flow,
              transportExtension: {
                ...flow.transportExtension,
                preAnnounceDuration: 0,
              },
            });
          }
        } else if (flow.type === 'CONSOLIDATION_STORAGE') {
          if (
            flow.consolidationStorageExtension &&
            flow.consolidationStorageExtension.preAnnounceDuration &&
            flow.consolidationStorageExtension.preAnnounceDuration > 0
          ) {
            updatedFlows.push({
              ...flow,
              consolidationStorageExtension: {
                ...flow.consolidationStorageExtension,
                preAnnounceDuration: 0,
              },
            });
          }
        }
      });

      if (updatedFlows.length > 0) {
        snackbarSvc.showMessage(t('interface:properties.flows.pre_announcement.messages.reset'));
        fddmSvc.updateFlows(updatedFlows);
      }
    },
    [selectedAreas, fddmSvc, getRelatedFlows, snackbarSvc, t],
  );

  const onParkingPriorityChange = useCallback(
    (newValue: number) => {
      const areas = areasToFlatCollection(selectedAreas);
      if (areas.length !== 1) return;
      const area = areas[0];
      if (!isSingularStationArea(area)) return;

      if (Number.isNaN(newValue)) return;

      fddmSvc.setPreference(
        'singularStationArea.loadHandlingPoiSpecs.parkingExtension.priority',
        newValue,
      );

      fddmSvc.updateArea({
        ...area,
        loadHandlingPoiSpecs: [
          {
            ...area.loadHandlingPoiSpecs[0],
            parkingExtension: {
              priority: newValue,
            },
          },
        ],
      });
    },
    [selectedAreas, fddmSvc],
  );

  if (loadHandlingPoiSpec === undefined) return null;

  return (
    <>
      <PropertiesLabel i18nkey='interface:properties.area.deltaX'>
        <NumberInput
          value={NumUtils.metersToMmRounded(loadHandlingPoiSpec.deltaX)}
          unit='mm'
          prefix={<GapHorizontalIcon />}
          sx={{ width: '100%' }}
          onChange={onDeltaXChange}
        />
      </PropertiesLabel>

      <PropertiesLabel i18nkey='interface:properties.area.deltaY'>
        <NumberInput
          value={NumUtils.metersToMmRounded(loadHandlingPoiSpec.deltaY)}
          unit='mm'
          prefix={<GapHorizontalIcon />}
          sx={{ width: '100%' }}
          onChange={onDeltaYChange}
        />
      </PropertiesLabel>
      <PropertiesLabel i18nkey='interface:properties.area.always_fit_station'>
        <Switch
          disabled={isDisabled}
          checked={loadHandlingPoiSpec.alwaysFitStation}
          onChange={onFitChange}
        />
      </PropertiesLabel>
      {showParkingSetting && (
        <PropertiesLabel i18nkey='interface:properties.area.can_be_used_for_parking'>
          <Switch
            disabled={isDisabled}
            checked={loadHandlingPoiSpec.canBeUsedForParking}
            onChange={onParkingChange}
          />
        </PropertiesLabel>
      )}
      {showParkingSetting &&
        loadHandlingPoiSpec.canBeUsedForParking &&
        loadHandlingPoiSpec.parkingExtension !== undefined && (
          <PropertiesLabel i18nkey='interface:properties.area.priority'>
            <NumberInput
              value={loadHandlingPoiSpec.parkingExtension.priority}
              sx={{ width: '100%' }}
              minValue={0}
              onChange={onParkingPriorityChange}
            />
          </PropertiesLabel>
        )}
    </>
  );
}

export const LoadHandlingPoiSpec = memo(LoadHandlingPoiSpecComponent);
